// Our own website analytics (041_site_analytics.sql), for the signed-out
// landing page only: how many people visit, where they came from, how far they
// read, what they click and whether they join the waitlist.
//
// Private by design (privacy.html, What we collect and On your device): no cookie and nothing
// written to the device. A visit is a random id held in this page's memory,
// gone on reload. Only the referring site's host and the UTM tags are sent,
// never a full URL, and the database keeps no IP. Production only, and never
// for a browser that asks not to be tracked (Global Privacy Control, Do Not
// Track) or is driven by automation.

import { supabase } from './supabase'
import { isProduction } from './env'

export type SiteEventKind = 'view' | 'section' | 'click' | 'waitlist'

export interface SiteEvent {
  visit: string
  kind: SiteEventKind
  path?: string
  lang?: string
  target?: string
  referrer?: string | null
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  device?: 'phone' | 'tablet' | 'desktop'
}

/** The referring site's host, without "www."; null for a visit from this site or none. */
export function referrerHost(referrer: string, ownHost: string): string | null {
  if (!referrer) return null
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '')
    return host && host !== ownHost.toLowerCase().replace(/^www\./, '') ? host : null
  } catch {
    return null
  }
}

/** The campaign tags of a landing URL (?utm_source=…), lower-cased and short. */
export function utmFrom(search: string): Pick<SiteEvent, 'utm_source' | 'utm_medium' | 'utm_campaign'> {
  const params = new URLSearchParams(search)
  const tag = (key: string) => params.get(key)?.trim().toLowerCase().slice(0, 60) || null
  return { utm_source: tag('utm_source'), utm_medium: tag('utm_medium'), utm_campaign: tag('utm_campaign') }
}

/** Device class from the viewport width alone, no user-agent string. */
export function deviceOf(width: number): 'phone' | 'tablet' | 'desktop' {
  return width < 600 ? 'phone' : width < 1024 ? 'tablet' : 'desktop'
}

/** Whether to count this visit at all. */
export function analyticsAllowed(env: { production: boolean; gpc?: boolean; dnt?: string | null; webdriver?: boolean }): boolean {
  return env.production && !env.gpc && env.dnt !== '1' && !env.webdriver
}

// ── the running visit ─────────────────────────────────────────────────────────

const BATCH = 20
let visit: string | null = null
let base: Omit<SiteEvent, 'visit' | 'kind'> = {}
const queue: SiteEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null

function flush() {
  if (timer) { clearTimeout(timer); timer = null }
  while (queue.length) {
    const batch = queue.splice(0, BATCH)
    // Best effort: a lost batch only lowers a count.
    void supabase.rpc('track', { p_events: batch }).then(() => {}, () => {})
  }
}

function push(event: Omit<SiteEvent, 'visit'>) {
  if (!visit) return
  queue.push({ visit, ...event })
  if (queue.length >= BATCH) flush()
  else if (!timer) timer = setTimeout(flush, 3000)
}

/** Counts something on the running visit (a waitlist signup); nothing when analytics are off. */
export function trackEvent(event: { kind: SiteEventKind; target?: string }) {
  push({ path: base.path, lang: base.lang, ...event })
}

/** Starts counting this page's visit; returns the cleanup. */
export function startSiteAnalytics({ lang, allowed }: { lang: string; allowed?: boolean }): () => void {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean }
  const ok = allowed ?? analyticsAllowed({ production: isProduction, gpc: nav.globalPrivacyControl, dnt: nav.doNotTrack, webdriver: nav.webdriver })
  if (!ok || visit) return () => {}

  visit = crypto.randomUUID()
  base = { path: location.pathname, lang }
  push({ kind: 'view', ...base, referrer: referrerHost(document.referrer, location.hostname), ...utmFrom(location.search), device: deviceOf(innerWidth) })

  // How far down people read: each [data-section] once, when 40 % of it is in view.
  const seen = new Set<string>()
  const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
    for (const e of entries) {
      const id = (e.target as HTMLElement).dataset.section
      if (e.isIntersecting && id && !seen.has(id)) { seen.add(id); push({ kind: 'section', path: base.path, target: id }) }
    }
  }, { threshold: 0.4 })
  document.querySelectorAll<HTMLElement>('[data-section]').forEach(el => observer?.observe(el))

  // What they click: anything marked data-track.
  const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest?.('[data-track]') as HTMLElement | null
    if (el?.dataset.track) push({ kind: 'click', path: base.path, target: el.dataset.track })
  }
  const onHide = () => { if (document.visibilityState === 'hidden') flush() }
  document.addEventListener('click', onClick, true)
  document.addEventListener('visibilitychange', onHide)

  return () => {
    observer?.disconnect()
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('visibilitychange', onHide)
    flush()
    visit = null
  }
}
